import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logout } from "../../utility/auth";
import  fetchData  from "../../functions/fetchData";
import axios from "axios";
import { getHeaders } from "../../utility/auth";
import useBillingDate from "../../hooks/useBillingDate";
import { BillingDateDisplay } from "../../components/BillingDateProvider";
import "./CashierSummary.css";
import { toBangkokSql, formatBangkokTime, bangkokDurationMinutes } from '../../utility/bangkokTime';

export default function CashierSummary() {
  const navigate = useNavigate();
  const { billingDate, isLoading: isLoadingBillingDate, refreshBillingDate } = useBillingDate();
  const [loading, setLoading] = useState(false);
  const [actualCashCount, setActualCashCount] = useState('');
  const [cashNotes, setCashNotes] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCounterAlreadyClosed, setIsCounterAlreadyClosed] = useState(false);
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalTransactions: 0,
    cashTransactions: 0,
    cashItems: 0,
    paymentBreakdown: {},
    topSellingItems: [],
    sessionStartTime: null,
    sessionEndTime: toBangkokSql()
  });

  // Get cashier info
  const cashierId = localStorage.getItem('uname') || sessionStorage.getItem('uname') || localStorage.getItem('user_id') || sessionStorage.getItem('user_id');
  const cashierName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier';

  const fetchCashierSummary = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use billing date instead of current system date
      const businessDate = billingDate || toBangkokSql().slice(0, 10);
      
      // First check if cashier already closed counter for business date
      const existingClosedSummary = await fetchData('cashier_summaries', null, 'id', {
        cashier_id: cashierId,
        setup_date: businessDate,
        status: 'closed'
      });

      if (existingClosedSummary && existingClosedSummary.length > 0) {
        setIsCounterAlreadyClosed(true);
        const closedTime = formatBangkokTime(existingClosedSummary[0].session_end_time);
        toast.info(`Counter was already closed at ${closedTime}. Only logout is available.`, {
          position: "top-center",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }
      
      // Fetch cashier's bills using business date from database
      const bills = await fetchData('bills', null, 'id', {
        setup_date: businessDate,
        cashier_id: cashierId
      });

      if (bills && bills.length > 0) {
        // Filter out cancelled/refunded bills before calculating summary statistics
        const activeBills = bills.filter(bill => 
          bill.status !== 'cancelled' && bill.status !== 'refunded'
        );
        
        // Calculate summary statistics - EXCLUDE entertainment bills from total sales
        const totalSales = activeBills
          .filter(bill => (bill.payment_method || '').toLowerCase() !== 'entertainment')
          .reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0);
        const totalTransactions = activeBills.length;
        const cashTransactions = activeBills.filter(
          bill => (bill.payment_method || '').toLowerCase() === 'cash'
        ).length;
        
        // Payment method breakdown with counts (includes active bills only, excluding cancelled)
        const paymentBreakdown = {};
        const paymentCounts = {};
        
        activeBills.forEach(bill => {
          const method = bill.payment_method || 'unknown';
          paymentBreakdown[method] = (paymentBreakdown[method] || 0) + parseFloat(bill.total_amount || 0);
          paymentCounts[method] = (paymentCounts[method] || 0) + 1;
        });

        // Get session start time (earliest active bill today)
        const sessionStartTime = activeBills.length > 0 
          ? activeBills.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0].created_at
          : toBangkokSql();

        // Fetch all bill items for these active bills only
        const billIds = activeBills.map(bill => bill.id);
        const cashBillIds = new Set(
          activeBills
            .filter(bill => (bill.payment_method || '').toLowerCase() === 'cash')
            .map(bill => String(bill.id))
        );
        const itemSales = {};
        let totalItems = 0;
        let cashItems = 0;
        
        if (billIds.length > 0) {
          try {
            // Fetch bill items using fetchData
            const billItems = await fetchData('bill_items', null, 'id', {
              bill_id: billIds.join(',')
            });
            
            if (billItems && billItems.length > 0) {
              billItems.forEach(item => {
                const itemName = item.item_name || 'Unknown Item';
                const quantity = parseInt(item.quantity || 0);
                const totalPrice = parseFloat(item.total_price || 0);
                
                totalItems += quantity;
                if (cashBillIds.has(String(item.bill_id))) {
                  cashItems += quantity;
                }
                
                if (itemSales[itemName]) {
                  itemSales[itemName].quantity += quantity;
                  itemSales[itemName].total += totalPrice;
                  itemSales[itemName].transactions += 1;
                } else {
                  itemSales[itemName] = {
                    quantity: quantity,
                    total: totalPrice,
                    unitPrice: parseFloat(item.unit_price || 0),
                    transactions: 1
                  };
                }
              });
            }
          } catch (error) {
            console.error('Error fetching bill items:', error);
          }
        }

        // Convert to sorted array for top selling items
        const topSellingItems = Object.entries(itemSales)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 10);

        // Calculate cash handling summary (entertainment excluded from totals)
        const cashTotal = paymentBreakdown.cash || 0;
        const cardTotal = paymentBreakdown.card || 0;
        const qrTotal = paymentBreakdown.qr || 0;
        const upiTotal = paymentBreakdown.upi || 0;
        const entertainmentTotal = paymentBreakdown.entertainment || 0;
        // nonCashTotal calculation: totalSales already excludes entertainment, so just subtract cash
        const nonCashTotal = totalSales - cashTotal;
        
        setSummaryData({
          totalSales,
          totalTransactions,
          cashTransactions,
          totalItems,
          cashItems,
          paymentBreakdown,
          paymentCounts,
          topSellingItems,
          sessionStartTime,
          sessionEndTime: toBangkokSql(),
          cashTotal,
          cardTotal,
          qrTotal,
          upiTotal,
          nonCashTotal,
          bills: activeBills.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        });
      } else {
        toast.info('No transactions found for today');
        setSummaryData({
          totalSales: 0,
          totalTransactions: 0,
          cashTransactions: 0,
          totalItems: 0,
          cashItems: 0,
          paymentBreakdown: {},
          paymentCounts: {},
          topSellingItems: [],
          sessionStartTime: toBangkokSql(),
          sessionEndTime: toBangkokSql(),
          cashTotal: 0,
          cardTotal: 0,
          qrTotal: 0,
          upiTotal: 0,
          nonCashTotal: 0,
          bills: []
        });
      }
    } catch (error) {
      console.error('Error fetching cashier summary:', error);
      toast.error('Failed to load summary data. Please check your connection.');
      // Set empty data on error
      setSummaryData({
        totalSales: 0,
        totalTransactions: 0,
        cashTransactions: 0,
        totalItems: 0,
        cashItems: 0,
        paymentBreakdown: {},
        paymentCounts: {},
        topSellingItems: [],
        sessionStartTime: toBangkokSql(),
        sessionEndTime: toBangkokSql(),
        cashTotal: 0,
        cardTotal: 0,
        qrTotal: 0,
        upiTotal: 0,
        nonCashTotal: 0,
        bills: []
      });
    } finally {
      setLoading(false);
    }
  }, [cashierId, billingDate]);

  useEffect(() => {
    if (billingDate) {
      fetchCashierSummary();
    }
  }, [fetchCashierSummary, billingDate]);

  const handleCloseCashier = async () => {
    try {
      setLoading(true);
      
      // Use billing date instead of current system date
      const businessDate = billingDate || toBangkokSql().slice(0, 10);
      
      // Check if counter is already closed - this should not happen in normal flow
      // as the billing page should redirect to summary when counter is closed
      const existingSummary = await fetchData('cashier_summaries', null, 'id', {
        cashier_id: cashierId,
        setup_date: businessDate,
        status: 'closed'
      });

      if (existingSummary && existingSummary.length > 0) {
        setErrorMessage(`Counter already closed today at ${formatTime(existingSummary[0].session_end_time)}. Cannot close again for the same date.`);
        setShowErrorModal(true);
        return;
      }

      // Calculate cash difference if actual cash is entered
      let cashDifference = 0;
      let actualCash = parseFloat(actualCashCount) || 0;
      if (actualCashCount) {
        cashDifference = actualCash - (summaryData.cashTotal || 0);
      }

      // Use the actual moment of closing, not the stale timestamp captured when the
      // summary page last loaded/refreshed - otherwise duration is calculated short.
      const actualCloseTime = toBangkokSql();

      // Calculate session duration
      const sessionDuration = bangkokDurationMinutes(summaryData.sessionStartTime, actualCloseTime);

      // Prepare summary data for database
      const summaryToSave = {
        cashier_id: cashierId,
        cashier_name: cashierName,
        setup_date: businessDate,
        session_start_time: summaryData.sessionStartTime,
        session_end_time: actualCloseTime,
        total_sales: summaryData.totalSales || 0,
        total_transactions: summaryData.totalTransactions || 0,
        total_items: summaryData.totalItems || 0,
        cash_total: summaryData.cashTotal || 0,
        card_total: summaryData.cardTotal || 0,
        qr_total: summaryData.qrTotal || 0,
        upi_total: summaryData.upiTotal || 0,
        non_cash_total: summaryData.nonCashTotal || 0,
        payment_breakdown: JSON.stringify(summaryData.paymentBreakdown || {}),
        payment_counts: JSON.stringify(summaryData.paymentCounts || {}),
        top_selling_items: JSON.stringify(summaryData.topSellingItems || []),
        recent_transactions: JSON.stringify((summaryData.bills || []).slice(0, 20)),
        actual_cash_count: actualCash > 0 ? actualCash : null,
        cash_difference: Math.abs(cashDifference) > 0 ? cashDifference : null,
        reconciliation_notes: cashNotes || null,
        session_duration_minutes: sessionDuration,
        average_transaction_amount: summaryData.totalTransactions > 0 
          ? (summaryData.totalSales / summaryData.totalTransactions) 
          : 0,
        status: 'closed'
      };

      // Save cashier summary to database
      const saveResult = await axios.post(
        '/insertdata/cashier_summaries',
        {
          cashier_id: summaryToSave.cashier_id,
          cashier_name: summaryToSave.cashier_name,
          setup_date: summaryToSave.setup_date,
          session_start_time: summaryToSave.session_start_time,
          session_end_time: summaryToSave.session_end_time,
          total_sales: summaryToSave.total_sales,
          total_transactions: summaryToSave.total_transactions,
          total_items: summaryToSave.total_items,
          cash_total: summaryToSave.cash_total,
          card_total: summaryToSave.card_total,
          qr_total: summaryToSave.qr_total,
          upi_total: summaryToSave.upi_total,
          non_cash_total: summaryToSave.non_cash_total,
          payment_breakdown: summaryToSave.payment_breakdown,
          payment_counts: summaryToSave.payment_counts,
          top_selling_items: summaryToSave.top_selling_items,
          recent_transactions: summaryToSave.recent_transactions,
          actual_cash_count: summaryToSave.actual_cash_count,
          cash_difference: summaryToSave.cash_difference,
          reconciliation_notes: summaryToSave.reconciliation_notes,
          session_duration_minutes: summaryToSave.session_duration_minutes,
          average_transaction_amount: summaryToSave.average_transaction_amount,
          status: summaryToSave.status
        },
        getHeaders()
      );

      if (saveResult && saveResult.data && saveResult.data.success !== false) {
        toast.success('Cashier summary saved successfully!');
        
        // Clear any remaining cart data
        localStorage.removeItem('amusementParkCart');
        localStorage.removeItem('amusementParkTickets');
        
        // Log the cashier session end
        console.log('Cashier session ended and saved:', {
          cashier: cashierName,
          endTime: toBangkokSql(),
          summaryId: saveResult.data.id || 'saved',
          summary: summaryToSave
        });

        toast.success('Cashier counter closed successfully!');
        
        // Logout and redirect to login after a delay
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        throw new Error(saveResult?.data?.message || 'Failed to save cashier summary');
      }
      
    } catch (error) {
      console.error('Error closing cashier:', error);
      if (error.message.includes('Duplicate entry') || error.message.includes('unique')) {
        setErrorMessage('Counter already closed today. Cannot close again for the same date.');
        setShowErrorModal(true);
      } else {
        toast.error(`Error closing cashier counter: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  const handlePrintSummary = () => {
    printThermalReceipt();
  };

  const printThermalReceipt = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date();
    
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cashier Summary - ${cashierName}</title>
        <meta charset="UTF-8">
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.3;
            width: 80mm;
            margin: 0;
            padding: 5mm;
            background: white;
          }
          
          .receipt-header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 5px;
            margin-bottom: 10px;
          }
          
          .receipt-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          
          .receipt-subtitle {
            font-size: 12px;
            margin-bottom: 5px;
          }
          
          .section {
            margin-bottom: 10px;
            padding-bottom: 5px;
          }
          
          .section-title {
            font-weight: bold;
            text-decoration: underline;
            margin-bottom: 3px;
          }
          
          .info-line {
            display: flex;
            justify-content: space-between;
            margin-bottom: 1px;
          }
          
          .separator {
            border-top: 1px dashed #000;
            margin: 5px 0;
          }
          
          .total-line {
            font-weight: bold;
            font-size: 14px;
          }
          
          .cash-section {
            background: #f0f0f0;
            padding: 5px;
            margin: 5px 0;
            border: 1px solid #000;
          }
          
          .item-line {
            font-size: 10px;
            margin-bottom: 1px;
          }
          
          .footer {
            text-align: center;
            margin-top: 10px;
            font-size: 10px;
          }
          
          .signature-section {
            margin-top: 20px;
            border-top: 1px solid #000;
            padding-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <div class="receipt-title">🎡 THE VIEW PATTAYA</div>
          <div class="receipt-subtitle">CASHIER SUMMARY REPORT</div>
          <div>Date: ${currentDate.toLocaleDateString()}</div>
          <div>Time: ${currentDate.toLocaleTimeString()}</div>
        </div>

        <div class="section">
          <div class="section-title">CASHIER INFORMATION</div>
          <div class="info-line">
            <span>Cashier:</span>
            <span>${cashierName}</span>
          </div>
          <div class="info-line">
            <span>Session Start:</span>
            <span>${formatTime(summaryData.sessionStartTime)}</span>
          </div>
          <div class="info-line">
            <span>Session End:</span>
            <span>${formatTime(summaryData.sessionEndTime)}</span>
          </div>
          <div class="info-line">
            <span>Duration:</span>
            <span>${bangkokDurationMinutes(summaryData.sessionStartTime, summaryData.sessionEndTime)} min</span>
          </div>
        </div>

        <div class="separator"></div>

        <div class="section">
          <div class="section-title">SALES SUMMARY</div>
          <div class="info-line">
            <span>Total Transactions:</span>
            <span>${summaryData.cashTransactions || 0}</span>
          </div>
          <div class="info-line">
            <span>Total Items Sold:</span>
            <span>${summaryData.cashItems || 0}</span>
          </div>
          <div class="info-line total-line">
            <span>TOTAL SALES:</span>
            <span>${formatCurrency(summaryData.cashTotal || 0)}</span>
          </div>
          <div class="info-line">
            <span>Average Transaction:</span>
            <span>${formatCurrency((summaryData.cashTransactions || 0) > 0 ? (summaryData.cashTotal || 0) / summaryData.cashTransactions : 0)}</span>
          </div>
        </div>

        <div class="separator"></div>

        <div class="section">
          <div class="section-title">PAYMENT METHODS</div>
          <div class="info-line total-line">
            <span>Cash:</span>
            <span>${formatCurrency(summaryData.cashTotal || 0)}</span>
          </div>
          <div class="info-line total-line">
            <span>Card:</span>
            <span>${formatCurrency(summaryData.cardTotal || 0)}</span>
          </div>
          <div class="info-line total-line">
            <span>QR:</span>
            <span>${formatCurrency(summaryData.qrTotal || 0)}</span>
          </div>
          <div class="info-line total-line">
            <span>UPI:</span>
            <span>${formatCurrency(summaryData.upiTotal || 0)}</span>
          </div>
          <div class="separator"></div>
          <div class="info-line total-line">
            <span>TOTAL SALES:</span>
            <span>${formatCurrency(summaryData.totalSales || 0)}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">💵 CASH RECONCILIATION</div>
          <div class="info-line total-line">
            <span>Cash Sales:</span>
            <span>${formatCurrency(summaryData.cashTotal || 0)}</span>
          </div>
          <div class="info-line">
            <span>Expected Cash:</span>
            <span>${formatCurrency(summaryData.cashTotal || 0)}</span>
          </div>
          <div class="info-line">
            <span>Actual Cash:</span>
            <span>${actualCashCount !== '' ? formatCurrency(parseFloat(actualCashCount)) : '____________'}</span>
          </div>
          <div class="info-line">
            <span>Difference:</span>
            <span>${actualCashCount !== '' ? 
              `${formatCurrency(Math.abs(calculateCashDifference()))} ${calculateCashDifference() > 0 ? '(Over)' : calculateCashDifference() < 0 ? '(Short)' : '(Exact)'}` 
              : '____________'}</span>
          </div>
          ${cashNotes ? `
          <div class="info-line">
            <span>Notes:</span>
            <span>${cashNotes}</span>
          </div>
          ` : ''}
        </div>

        <div class="separator"></div>

        <div class="section">
          <div class="section-title">TRANSACTION DETAILS</div>
          ${summaryData.bills.slice(0, 10).map(bill => `
            <div class="item-line">
              ${bill.bill_number} - ${formatTime(bill.created_at)} - ${formatCurrency(bill.total_amount)}
            </div>
          `).join('')}
          ${summaryData.bills.length > 10 ? `<div class="item-line">... and ${summaryData.bills.length - 10} more transactions</div>` : ''}
        </div>

        <div class="signature-section">
          <div class="section-title">VERIFICATION</div>
          <div style="margin-top: 10px;">
            <div>Cashier Signature: ___________________</div>
            <br>
            <div>Supervisor Signature: ___________________</div>
            <br>
            <div>Date: ___________________</div>
          </div>
        </div>

        <div class="footer">
          <div class="separator"></div>
          <div>Generated: ${currentDate.toLocaleString()}</div>
          <div>System: View Park v2.0</div>
          <div>** Please retain for records **</div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    
    // Auto print after loading
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const formatCurrency = (amount) => {
    return `฿${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatTime = (timestamp) => {
    return formatBangkokTime(timestamp);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Calculate cash difference
  const calculateCashDifference = () => {
    const expectedCash = summaryData.cashTotal || 0;
    const actualCash = parseFloat(actualCashCount) || 0;
    return actualCash - expectedCash;
  };

  // Handle actual cash input change
  const handleActualCashChange = (e) => {
    setActualCashCount(e.target.value);
  };

  // Handle cash notes change
  const handleCashNotesChange = (e) => {
    setCashNotes(e.target.value);
  };

  return (
    <div className="cashier-summary-container">
      <ToastContainer />
      
      {/* Counter Already Closed View */}
      {isCounterAlreadyClosed ? (
        <div className="counter-closed-view">
          <div className="closed-message-card">
            <div className="closed-icon">🔒</div>
            <h2>Counter Already Closed</h2>
            <p>Your cashier counter has been closed for today.</p>
            <p className="closed-info">No further actions are available except logout.</p>
            <div className="logout-section">
              <button 
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  logout();
                  navigate('/login');
                }}
                className="logout-btn"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Error Modal */}
          {showErrorModal && (
            <div className="modal-overlay">
              <div className="error-modal">
                <div className="modal-header">
                  <h3>⚠️ Counter Already Closed</h3>
                </div>
                <div className="modal-body">
                  <p>{errorMessage}</p>
                </div>
                <div className="modal-footer">
                  <button 
                    onClick={closeErrorModal}
                    className="modal-btn ok-btn"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
      
          <div className="summary-header">
        <div className="summary-title">
          <h2>📊 Cashier Session Summary</h2>
          <div className="cashier-info">
            <span>👤 {cashierName}</span>
            <span>📅 {billingDate ? new Date(billingDate).toLocaleDateString() : formatDate(summaryData.sessionEndTime)}</span>
          </div>
        </div>
        
        <div className="summary-actions">
          <button 
            onClick={handlePrintSummary}
            className="action-btn print-btn"
          >
            🖨️ Print Summary
          </button>
          <button 
            onClick={printThermalReceipt}
            className="action-btn thermal-btn"
          >
            🧾 Print Thermal
          </button>
          <button 
            onClick={() => navigate('/pos/ticketbilling')}
            className="action-btn back-btn"
          >
            ← Back to POS
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading summary data...</p>
        </div>
      ) : (
        <div className="cashier-summary-content">
          {/* Session Information */}
          <div className="cashier-summary-card session-card">
            <h3>🕒 Session Information</h3>
            <div className="session-details">
              <div className="detail-item">
                <span className="label">Start Time:</span>
                <span className="value">{formatTime(summaryData.sessionStartTime)}</span>
              </div>
              <div className="detail-item">
                <span className="label">End Time:</span>
                <span className="value">{formatTime(summaryData.sessionEndTime)}</span>
              </div>
              <div className="detail-item">
                <span className="label">Duration:</span>
                <span className="value">
                  {bangkokDurationMinutes(summaryData.sessionStartTime, summaryData.sessionEndTime)} minutes
                </span>
              </div>
            </div>
          </div>

          {/* Sales Overview */}
          <div className="cashier-summary-card sales-card">
            <h3>💰 Cash Sales Overview</h3>
            <div className="sales-overview">
              <div className="overview-item total-sales">
                <div className="overview-label">Cash Sales</div>
                <div className="overview-value">{formatCurrency(summaryData.cashTotal || 0)}</div>
              </div>
              <div className="overview-item total-transactions">
                <div className="overview-label">Cash Transactions</div>
                <div className="overview-value">{summaryData.cashTransactions || 0}</div>
              </div>
              <div className="overview-item total-items">
                <div className="overview-label">Cash Items Sold</div>
                <div className="overview-value">{summaryData.cashItems || 0}</div>
              </div>
              <div className="overview-item avg-transaction">
                <div className="overview-label">Average Cash Transaction</div>
                <div className="overview-value">
                  {formatCurrency((summaryData.cashTransactions || 0) > 0 ? (summaryData.cashTotal || 0) / summaryData.cashTransactions : 0)}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Breakdown */}
          <div className="cashier-summary-card payment-card">
            <h3>💳 Payment Method Breakdown</h3>
            <div className="payment-breakdown">
                <div className="payment-item">
                  <div className="payment-method">
                    <span className="payment-icon">💵</span>
                    <span className="method-name">CASH</span>
                  </div>
                  <div className="payment-details">
                    <div className="payment-amount">{formatCurrency(summaryData.cashTotal || 0)}</div>
                    <div className="payment-count">{summaryData.cashTransactions || 0} transactions</div>
                  </div>
                  <div className="payment-percentage">
                    {summaryData.totalSales > 0 ? Math.round((summaryData.cashTotal || 0) / summaryData.totalSales * 100) : 0}%
                  </div>
                </div>
                <div className="payment-item">
                  <div className="payment-method">
                    <span className="payment-icon">💳</span>
                    <span className="method-name">CARD</span>
                  </div>
                  <div className="payment-details">
                    <div className="payment-amount">{formatCurrency(summaryData.cardTotal || 0)}</div>
                    <div className="payment-count">{summaryData.paymentCounts?.card || 0} transactions</div>
                  </div>
                  <div className="payment-percentage">
                    {summaryData.totalSales > 0 ? Math.round((summaryData.cardTotal || 0) / summaryData.totalSales * 100) : 0}%
                  </div>
                </div>
                <div className="payment-item">
                  <div className="payment-method">
                    <span className="payment-icon">📱</span>
                    <span className="method-name">QR</span>
                  </div>
                  <div className="payment-details">
                    <div className="payment-amount">{formatCurrency(summaryData.qrTotal || 0)}</div>
                    <div className="payment-count">{summaryData.paymentCounts?.qr || 0} transactions</div>
                  </div>
                  <div className="payment-percentage">
                    {summaryData.totalSales > 0 ? Math.round((summaryData.qrTotal || 0) / summaryData.totalSales * 100) : 0}%
                  </div>
                </div>
                <div className="payment-item">
                  <div className="payment-method">
                    <span className="payment-icon">📲</span>
                    <span className="method-name">UPI</span>
                  </div>
                  <div className="payment-details">
                    <div className="payment-amount">{formatCurrency(summaryData.upiTotal || 0)}</div>
                    <div className="payment-count">{summaryData.paymentCounts?.upi || 0} transactions</div>
                  </div>
                  <div className="payment-percentage">
                    {summaryData.totalSales > 0 ? Math.round((summaryData.upiTotal || 0) / summaryData.totalSales * 100) : 0}%
                  </div>
                </div>
            </div>
          </div>

          {/* Cash Reconciliation */}
          <div className="cashier-summary-card cash-reconciliation-card">
            <h3>💵 Cash Reconciliation</h3>
            <div className="cash-reconciliation">
              <div className="cash-summary">
                <div className="cash-item expected">
                  <div className="cash-label">Expected Cash</div>
                  <div className="cash-value">{formatCurrency(summaryData.cashTotal || 0)}</div>
                </div>
                <div className="cash-input-section">
                  <label htmlFor="actualCash">Actual Cash Count:</label>
                  <input 
                    type="number" 
                    id="actualCash" 
                    step="0.01" 
                    placeholder="0.00"
                    value={actualCashCount}
                    onChange={handleActualCashChange}
                    className="cash-input"
                  />
                </div>
                <div className="cash-difference">
                  {actualCashCount !== '' ? (
                    <div className={`difference-display ${calculateCashDifference() === 0 ? 'exact' : calculateCashDifference() > 0 ? 'over' : 'short'}`}>
                      <span className="difference-label">Difference:</span>
                      <span className="difference-amount">
                        {formatCurrency(Math.abs(calculateCashDifference()))}
                        {calculateCashDifference() > 0 && ' (Over)'}
                        {calculateCashDifference() < 0 && ' (Short)'}
                        {calculateCashDifference() === 0 && ' (Exact)'}
                      </span>
                    </div>
                  ) : (
                    <span>Difference will be calculated when you enter actual cash</span>
                  )}
                </div>
              </div>
              <div className="reconciliation-notes">
                <label htmlFor="cashNotes">Notes (if any difference):</label>
                <textarea 
                  id="cashNotes" 
                  rows="3" 
                  placeholder="Enter any notes about cash discrepancies..."
                  value={cashNotes}
                  onChange={handleCashNotesChange}
                  className="notes-textarea"
                ></textarea>
              </div>
            </div>
          </div>

          {/* Top Selling Items */}
          <div className="cashier-summary-card items-card">
            <h3>🎫 Top Selling Items</h3>
            <div className="top-items">
              {summaryData.topSellingItems.length === 0 ? (
                <p className="no-items">No items sold in this session</p>
              ) : (
                <div className="items-list">
                  {summaryData.topSellingItems.map((item, index) => (
                    <div key={item.name} className="item-row">
                      <div className="item-rank">#{index + 1}</div>
                      <div className="item-details">
                        <div className="item-name">{item.name}</div>
                        <div className="item-stats">
                          <span className="quantity">Qty: {item.quantity}</span>
                          <span className="total">{formatCurrency(item.total)}</span>
                          <span className="transactions">{item.transactions} sales</span>
                        </div>
                      </div>
                      <div className="item-unit-price">
                        {formatCurrency(item.unitPrice)} each
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="cashier-summary-card transactions-card">
            <h3>📋 Recent Transactions</h3>
            <div className="recent-transactions">
              {summaryData.bills && summaryData.bills.length === 0 ? (
                <p className="no-transactions">No transactions found</p>
              ) : (
                <div className="transactions-list">
                  <div className="transaction-header">
                    <span>Bill #</span>
                    <span>Time</span>
                    <span>Amount</span>
                  </div>
                  {(summaryData.bills || []).slice(0, 10).map((bill) => (
                    <div key={bill.id} className="transaction-row">
                      <div className="bill-number">{bill.bill_number}</div>
                      <div className="bill-time">{formatTime(bill.created_at)}</div>
                      <div className="bill-amount">{formatCurrency(bill.total_amount)}</div>
                    </div>
                  ))}
                  {summaryData.bills && summaryData.bills.length > 10 && (
                    <div className="more-transactions">
                      + {summaryData.bills.length - 10} more transactions
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Close Cashier Section */}
          <div className="close-cashier-section">
            <div className="close-warning">
              <h3>🔒 Close Cashier Counter</h3>
              <p>This will end your cashier session and log you out. Make sure all transactions are complete.</p>
              <button 
                onClick={handleCloseCashier}
                className="close-cashier-btn"
              >
                🔚 Close Counter & Logout
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
